import { compact, formatYearRange, normalizeMediaText, toNullableNumber, uniqueBy } from './media.js'

const KINOBD_TYPES = Object.freeze({
    film: 'movie',
    serial: 'series',
})

const MAX_ARTWORK_CANDIDATES = 12
const MAX_HERO_BACKDROPS = 9
const MAX_HERO_LANDSCAPE_POSTERS = 3
const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/
const YOUTUBE_HOSTS = new Set([
    'youtube.com',
    'www.youtube.com',
    'm.youtube.com',
    'youtube-nocookie.com',
    'www.youtube-nocookie.com',
])
const YOUTU_BE_HOSTS = new Set(['youtu.be', 'www.youtu.be'])
const VK_TRAILER_KINDS = new Set(['pv', 'character_trailer'])
const VK_HOSTS = ['vk.com', 'vk.ru', 'vkvideo.ru']

function absoluteHttpUrl(value) {
    if (!/^https?:\/\//i.test(String(value || ''))) return null
    try {
        return new URL(value).toString()
    } catch (_) {
        return null
    }
}

function personName(person) {
    return (person && (person.name_russian || person.name_english || person.name)) || ''
}

function imageList(values, {
    landscape = false,
    hdOnly = false,
    preserveProviderOrder = false,
    limit = 12,
} = {}) {
    let images = (Array.isArray(values) ? values : [])
        .map((image, index) => ({
            url: absoluteHttpUrl(image && (image.url || image.src)),
            width: toNullableNumber(image && image.width),
            height: toNullableNumber(image && image.height),
            index,
        }))
        .filter((image) => {
            if (!image.url) return false
            if (!landscape) return true
            return !image.width || !image.height || image.width / image.height >= 1.35
        })

    if (hdOnly) {
        images = images.filter((image) => {
            const hasDimensions = image.width > 0 && image.height > 0
            /* Some KinoBD image records omit dimensions even though the
               original is a valid HD backdrop. Keep those candidates for the
               browser to validate via naturalWidth/naturalHeight. Known
               portrait and sub-HD assets are still discarded immediately. */
            return !hasDimensions || (
                image.width >= 1280
                && image.height >= 720
                && image.width / image.height >= 1.35
            )
        })
    }

    const uniqueImages = uniqueBy(images, (image) => image.url)
    if (!preserveProviderOrder) {
        uniqueImages.sort((first, second) => {
            const firstArea = Number(first.width || 0) * Number(first.height || 0)
            const secondArea = Number(second.width || 0) * Number(second.height || 0)
            return secondArea - firstArea
                || Number(second.width || 0) - Number(first.width || 0)
                || first.index - second.index
        })
    }

    return uniqueImages.slice(0, limit)
        .map(({ index: _index, ...image }) => image)
}

function resilientProviderSample(images, limit) {
    if (!Array.isArray(images) || images.length <= limit) return (images || []).slice(0, limit)
    const selectedIndexes = new Set()
    for (let index = 0; index < Math.min(7, images.length, limit); index += 1) {
        selectedIndexes.add(index)
    }
    /* KinoBD can expose long runs of stale CDN URLs. Keep provider order, but
       reserve two bounded probes deeper in the list instead of persisting
       only the first broken run. */
    for (const index of [15, 31]) {
        if (index < images.length && selectedIndexes.size < limit) selectedIndexes.add(index)
    }
    for (let index = 0; index < images.length && selectedIndexes.size < limit; index += 1) {
        selectedIndexes.add(index)
    }
    return [...selectedIndexes].sort((left, right) => left - right).map((index) => images[index])
}

function youtubeVideoId(value) {
    const candidate = value && typeof value === 'object'
        ? (value.embedUrl || value.playerUrl || value.videoUrl || value.iframe || value.src || value.url || '')
        : value
    if (!candidate) return null

    let url
    try {
        const normalized = String(candidate).startsWith('//')
            ? `https:${candidate}`
            : String(candidate)
        url = new URL(normalized)
    } catch (_) {
        return null
    }
    if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port) return null

    const host = url.hostname.toLowerCase()
    let id = ''
    if (YOUTU_BE_HOSTS.has(host)) {
        const segments = url.pathname.split('/').filter(Boolean)
        if (segments.length !== 1) return null
        id = segments[0]
    } else if (YOUTUBE_HOSTS.has(host)) {
        const segments = url.pathname.split('/').filter(Boolean)
        if (segments[0] === 'watch' && segments.length === 1) {
            id = url.searchParams.get('v') || ''
        } else if (['embed', 'shorts', 'live'].includes(segments[0]) && segments.length === 2) {
            id = segments[1]
        }
    }
    return YOUTUBE_VIDEO_ID.test(id) ? id : null
}

export function normalizeYouTubeTrailer(value) {
    let videoId = youtubeVideoId(value)
    const hasSuppliedUrl = Boolean(value && typeof value === 'object'
        && (value.embedUrl || value.playerUrl || value.videoUrl
            || value.iframe || value.src || value.url))
    if (!videoId && !hasSuppliedUrl && value && typeof value === 'object' && value.provider === 'youtube') {
        const rawId = String(value.videoId || value.id || '').replace(/^youtube:/, '')
        if (YOUTUBE_VIDEO_ID.test(rawId)) videoId = rawId
    }
    if (!videoId) return null

    const embed = new URL(`https://www.youtube-nocookie.com/embed/${videoId}`)
    embed.searchParams.set('autoplay', '1')
    embed.searchParams.set('mute', '1')
    embed.searchParams.set('loop', '1')
    embed.searchParams.set('playlist', videoId)
    embed.searchParams.set('playsinline', '1')
    embed.searchParams.set('enablejsapi', '1')
    return {
        id: `youtube:${videoId}`,
        provider: 'youtube',
        kind: 'trailer',
        videoId,
        embedUrl: embed.toString(),
        poster: `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
    }
}

export function normalizeKinoBdTrailers(item) {
    if (!item || typeof item !== 'object') return []

    const suppliedTrailers = Array.isArray(item.trailers)
        ? item.trailers
        : item.trailers && typeof item.trailers === 'object'
            ? Object.values(item.trailers)
            : []
    const suppliedCandidates = [
        item.trailer,
        item.trailer_url,
        item.trailerUrl,
        ...suppliedTrailers,
        ...[
            item.yt_video_id,
            item.youtube_video_id,
            item.youtubeVideoId,
        ].filter(Boolean).map((videoId) => ({ provider: 'youtube', videoId })),
    ]
    const seen = new Set()
    return suppliedCandidates.map(normalizeYouTubeTrailer).filter((trailer) => {
        if (!trailer || seen.has(trailer.videoId)) return false
        seen.add(trailer.videoId)
        return true
    }).slice(0, 6)
}

export function normalizeKinoBdTrailer(item) {
    return normalizeKinoBdTrailers(item)[0] || null
}

export function normalizeVkTrailer(value) {
    if (!value || typeof value !== 'object') return null
    const kind = String(value.kind || '').toLowerCase()
    if (!VK_TRAILER_KINDS.has(kind)) return null

    const candidate = value.playerUrl || value.embedUrl || value.url
    let url
    try {
        const normalized = String(candidate || '').startsWith('//')
            ? `https:${candidate}`
            : String(candidate || '').replace(/^http:/i, 'https:')
        url = new URL(normalized)
    } catch (_) {
        return null
    }
    const host = url.hostname.toLowerCase()
    const allowedHost = VK_HOSTS.some((domain) => host === domain || host.endsWith(`.${domain}`))
    if (
        url.protocol !== 'https:'
        || url.username
        || url.password
        || url.port
        || !allowedHost
        || !/^\/video_e(?:xt|mbed)\.php$/i.test(url.pathname)
    ) return null

    url.searchParams.set('autoplay', '1')
    url.searchParams.set('loop', '1')
    url.searchParams.set('js_api', '1')
    url.searchParams.set('muted', '1')
    url.searchParams.set('hd', '4')
    const poster = absoluteHttpUrl(value.imageUrl || value.poster)
    return {
        id: String(value.id || url.toString()),
        provider: 'vk',
        kind,
        embedUrl: url.toString(),
        poster,
    }
}

function boundedArtworkCandidates(values, poster) {
    const candidates = uniqueBy((values || [])
        .map((image) => {
            const url = absoluteHttpUrl(image && (image.url || image))
            if (!url) return null
            return {
                url,
                width: toNullableNumber(image && image.width),
                height: toNullableNumber(image && image.height),
            }
        })
        .filter(Boolean), (image) => image.url)
    const posterUrl = absoluteHttpUrl(poster)
    const withoutPoster = posterUrl
        ? candidates.filter((image) => image.url !== posterUrl)
        : candidates
    const bounded = withoutPoster.slice(0, posterUrl
        ? MAX_ARTWORK_CANDIDATES - 1
        : MAX_ARTWORK_CANDIDATES)
    if (posterUrl) bounded.push({ url: posterUrl, width: null, height: null })
    return bounded
}

function kinoBdSearchScore(item, query) {
    let normalizedQuery = normalizeMediaText(query)
    let names = [item && item.name_russian, item && item.name_original]
        .map(normalizeMediaText)
        .filter(Boolean)
    if (names.some((name) => name === normalizedQuery)) return 0
    if (names.some((name) => name.startsWith(`${normalizedQuery} `))) return 1
    if (names.some((name) => name.includes(normalizedQuery))) return 2
    return 3
}

export function normalizeKinoBdSummary(item) {
    let kind = KINOBD_TYPES[item && item.type]
    let id = toNullableNumber(item && item.id)
    if (!kind || !id) return null

    let yearStart = toNullableNumber(item.year_start || item.year)
    let yearEnd = toNullableNumber(item.year_end)
    let title = item.name_russian || item.name_original || 'Без названия'
    let kindLabel = kind === 'series' ? 'Сериал' : 'Фильм'
    let meta = compact([
        kindLabel,
        formatYearRange(yearStart, yearEnd),
        item.rating_kp ? `КП ${item.rating_kp}` : '',
    ])

    const trailerCandidates = normalizeKinoBdTrailers(item)
    return {
        key: `kinobd:${id}`,
        provider: 'kinobd',
        id: String(id),
        kind,
        title,
        originalTitle: item.name_original || '',
        year: yearStart,
        yearEnd,
        poster: absoluteHttpUrl(item.best_poster || item.big_poster || item.small_poster),
        trailer: trailerCandidates[0] || null,
        trailerCandidates,
        subtitle: meta.join(' · '),
        releaseType: item.type,
        isEpisodic: kind === 'series',
        ids: {
            kinopoisk: toNullableNumber(item.kinopoisk_id),
            imdb: item.imdb_id || null,
            tmdb: toNullableNumber(item.tmdb_id),
            shikimori: null,
        },
    }
}

export function normalizeKinoBdSearch(items, query, limit = 20) {
    return (Array.isArray(items) ? items : [])
        .map((item, index) => ({ item, index, summary: normalizeKinoBdSummary(item) }))
        .filter(({ summary }) => Boolean(summary))
        .sort((first, second) => {
            return kinoBdSearchScore(first.item, query) - kinoBdSearchScore(second.item, query)
                || first.index - second.index
        })
        .map(({ summary }) => summary)
        .filter((summary, index, results) => results.findIndex((item) => item.key === summary.key) === index)
        .slice(0, limit)
}

export function normalizeKinoBdDetails(item, imageResponse = {}, creditsResponse = {}) {
    let summary = normalizeKinoBdSummary(item)
    if (!summary) return null
    summary = {
        ...summary,
        poster: absoluteHttpUrl(item.best_poster || item.big_poster || item.small_poster) || summary.poster,
    }

    let posters = imageList(imageResponse.posters, { limit: 12 })
    /* KinoBD frequently leaves the largest files in its response after they
       have disappeared from the CDN. Sorting by pixel area and slicing first
       therefore discarded smaller, working Full HD files (Dexter is a real
       example). Provider order gives the runtime several independent chances
       before falling back. The API also classifies occasional wide key art as
       a poster (e.g. Zhmurki), so reserve a few candidate slots for those. */
    const providerBackdrops = imageList(imageResponse.backdrops, {
        landscape: true,
        hdOnly: true,
        preserveProviderOrder: true,
        limit: Number.MAX_SAFE_INTEGER,
    })
    let backdrops = resilientProviderSample(providerBackdrops, MAX_HERO_BACKDROPS)
    const landscapePosters = imageList(imageResponse.posters, {
        landscape: true,
        hdOnly: true,
        preserveProviderOrder: true,
        limit: MAX_HERO_LANDSCAPE_POSTERS,
    })
    const heroArtwork = uniqueBy([
        ...backdrops,
        ...landscapePosters,
    ], (image) => image.url)
    let stills = imageList(imageResponse.stills, { landscape: true, limit: 12 })
    let actors = uniqueBy(creditsResponse.cast || [], (person) => String(person && person.id || personName(person)))
        .map(personName)
        .filter(Boolean)
        .slice(0, 16)
    let directors = uniqueBy(creditsResponse.directors || [], (person) => String(person && person.id || personName(person)))
        .map(personName)
        .filter(Boolean)

    return {
        ...summary,
        description: item.description || '',
        slogan: item.slogan || '',
        durationMinutes: toNullableNumber(item.time_minutes),
        ageRating: item.age_restriction ? `${String(item.age_restriction).replace(/\+$/, '')}+` : '',
        premiere: item.premiere_ru || item.premiere_world || '',
        ratings: {
            kinopoisk: toNullableNumber(item.rating_kp),
            imdb: toNullableNumber(item.rating_imdb),
        },
        genres: compact((item.genres || []).map((genre) => genre && genre.name_ru)),
        countries: compact((item.countries || []).map((country) => country && country.name_ru)),
        directors,
        actors,
        images: { posters, backdrops, stills },
        backdrop: heroArtwork[0]?.url || summary.poster,
        /* Episodic stills are deliberately not hero artwork. The fallback is
           a bounded provider-order sample of HD backdrops, then any verified
           wide key art from posters, and finally the full-size portrait. */
        backdropCandidates: boundedArtworkCandidates(heroArtwork, summary.poster),
        detailsHydrated: true,
        playbackRef: {
            provider: 'kinobd',
            internalId: summary.id,
            kinopoiskId: summary.ids.kinopoisk,
            title: summary.title,
        },
    }
}
